import { __awaiter } from "tslib";
import { useState, useCallback } from 'react';
import { useFindOrderMutation, useFindGroupOrderMutation, useGetUserProfileQuery } from '@websky/graphql';
import { useSecureAccess } from '../../../../SecureAccess/SecureAccess.hook';
import { ApolloError } from 'apollo-boost';
import { useConfig } from '../../../../context';
export function useFindOrder({ onSuccess }) {
    var _a;
    const { data: userProfile, loading: userProfileLoading, refetch: getUserProfile } = useGetUserProfileQuery({
        fetchPolicy: 'no-cache'
    });
    const { global: { showBookingPreview } } = useConfig();
    const [findOrderMutation, { loading: findOrderLoading }] = useFindOrderMutation();
    const [findGroupOrderMutation, { loading: findGroupOrderLoading }] = useFindGroupOrderMutation();
    const secureAccess = useSecureAccess();
    const [orderPreview, setOrderPreview] = useState(null);
    const [groupOrderParams, setGroupOrderParams] = useState(null);
    const [groupOrderResponse, setGroupOrderResponse] = useState(null);
    const getIsAuthorizedUser = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (!userProfileLoading) {
            return !!(userProfile === null || userProfile === void 0 ? void 0 : userProfile.CurrentUser);
        }
        try {
            const { data } = yield getUserProfile();
            return !!(data === null || data === void 0 ? void 0 : data.CurrentUser);
        }
        catch (_b) {
            return false;
        }
    }), [userProfile, userProfileLoading, getUserProfile]);
    const onHideOrderPreview = useCallback(() => {
        setOrderPreview(null);
    }, [setOrderPreview]);
    const onFindOrder = useCallback((params) => __awaiter(this, void 0, void 0, function* () {
        var _c;
        onHideOrderPreview();
        const isAuthorized = yield getIsAuthorizedUser();
        const hasBookingPreview = showBookingPreview && isAuthorized;
        try {
            const { data } = yield findOrderMutation({
                variables: {
                    params: Object.assign(Object.assign({}, params), { saveInProfile: false })
                }
            });
            if (data === null || data === void 0 ? void 0 : data.FindOrder) {
                if (hasBookingPreview) {
                    setOrderPreview(data.FindOrder);
                }
                else {
                    onSuccess(data.FindOrder);
                }
                return true;
            }
            else {
                return false;
            }
        }
        catch (e) {
            if (e instanceof ApolloError && ((_c = e.graphQLErrors) === null || _c === void 0 ? void 0 : _c.length)) {
                const errors = e.graphQLErrors;
                const isGroupOrder = errors.some(error => {
                    var _a, _b;
                    return (((_a = error === null || error === void 0 ? void 0 : error.debugMessage) === null || _a === void 0 ? void 0 : _a.includes('group order')) ||
                        ((_b = error === null || error === void 0 ? void 0 : error.message) === null || _b === void 0 ? void 0 : _b.includes('group order')));
                });
                if (secureAccess.isNeedToOpenSecureAccessModal(errors)) {
                    secureAccess.open();
                    secureAccess.setSubmitParams({ params: { id: params.id, secret: params.secret } });
                }
                if (isGroupOrder) {
                    secureAccess.close();
                    const groupOrder = yield onFindGroupOrder({
                        id: params.id,
                        secret: params.secret,
                        travellersDataForSplit: []
                    });
                    if (groupOrder) {
                        setGroupOrderParams({ id: params.id, secret: params.secret });
                        setGroupOrderResponse(groupOrder);
                        return true;
                    }
                    secureAccess.close();
                }
            }
            return false;
        }
    }), [
        findOrderMutation,
        setGroupOrderParams,
        setGroupOrderResponse,
        getIsAuthorizedUser,
        onHideOrderPreview,
        secureAccess.isNeedToOpenSecureAccessModal,
        secureAccess.setSubmitParams,
        secureAccess.open,
        secureAccess.close
    ]);
    const onFindGroupOrder = useCallback((params) => __awaiter(this, void 0, void 0, function* () {
        try {
            const { data, errors } = yield findGroupOrderMutation({
                variables: { params }
            });
            if (secureAccess.isNeedToOpenSecureAccessModal(errors)) {
                secureAccess.setSubmitParams({
                    params: {
                        id: params.id,
                        secret: params.secret,
                        travellersDataForSplit: params.travellersDataForSplit
                    }
                });
                secureAccess.open();
            }
            if (data === null || data === void 0 ? void 0 : data.FindGroupOrder) {
                return data.FindGroupOrder;
            }
            return null;
        }
        catch (e) {
            console.log(e);
            return null;
        }
    }), [
        findGroupOrderMutation,
        secureAccess.isNeedToOpenSecureAccessModal,
        secureAccess.setSubmitParams,
        secureAccess.open
    ]);
    const onAcceptGroupOrder = useCallback((travellersDataForSplit) => onFindGroupOrder({
        id: groupOrderParams.id,
        secret: groupOrderParams.secret,
        travellersDataForSplit
    }), [onFindGroupOrder, groupOrderParams]);
    const onSubmitSecureAccess = useCallback((secret2fa) => __awaiter(this, void 0, void 0, function* () {
        if (secureAccess.isFindGroupOrderVariablesGuard(secureAccess.submitParams)) {
            yield onFindGroupOrder({
                id: secureAccess.submitParams.params.id,
                secret: secureAccess.submitParams.params.secret,
                travellersDataForSplit: secureAccess.submitParams.params.travellersDataForSplit
                // secret2fa
            });
        }
        else {
            yield onFindOrder({
                id: secureAccess.submitParams.params.id,
                secret: secureAccess.submitParams.params.secret,
                secret2fa
            });
        }
        secureAccess.setShowError(true);
    }), [onFindOrder, onFindGroupOrder, secureAccess]);
    const onResendSecureAccess = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        if (secureAccess.isFindGroupOrderVariablesGuard(secureAccess.submitParams)) {
            yield onFindGroupOrder({
                id: secureAccess.submitParams.params.id,
                secret: secureAccess.submitParams.params.secret,
                travellersDataForSplit: secureAccess.submitParams.params.travellersDataForSplit
            });
        }
        else {
            yield onFindOrder({
                id: secureAccess.submitParams.params.id,
                secret: secureAccess.submitParams.params.secret
            });
        }
    }), [onFindOrder, onFindGroupOrder, secureAccess]);
    return {
        // data
        orderPreview,
        secureAccess,
        groupOrderParams,
        groupOrderResponse,
        isLoading: findOrderLoading || findGroupOrderLoading,
        isAuthorized: !!(userProfile === null || userProfile === void 0 ? void 0 : userProfile.CurrentUser),
        /** Пользователь залогинен и является владельцем найденого заказа */
        isOrderOwner: (orderPreview === null || orderPreview === void 0 ? void 0 : orderPreview.ownerId) === ((_a = userProfile === null || userProfile === void 0 ? void 0 : userProfile.CurrentUser) === null || _a === void 0 ? void 0 : _a.userId),
        // methods
        setGroupOrderParams,
        setGroupOrderResponse,
        onFindOrder,
        onFindGroupOrder,
        onAcceptGroupOrder,
        onSubmitSecureAccess,
        onResendSecureAccess,
        onHideOrderPreview
    };
}
